import * as React from "react";

import { useMeasure } from "@/shared/hooks";

import type {
  MeasureOverlayProjection,
  ToolHover,
  ToolMode,
} from "./BabylonManager";
import { CoordinateTooltip } from "./CoordinateTooltip";
import { MeasureOverlay } from "./MeasureOverlay";
import styles from "./ToolOverlays.module.css";

/**
 * The per-frame reports the measure tool produces. `ToolOverlays` fills a
 * caller's ref with these so the manager can push each report straight into
 * the leaf that draws it.
 */
export interface ToolOverlaySink {
  onToolHover: (hover: ToolHover | null) => void;
  onMeasureOverlay: (overlay: MeasureOverlayProjection | null) => void;
}

interface ToolOverlaysProps {
  /**
   * Filled with this component's setters while it is mounted, nulled when
   * it unmounts. A ref rather than props because the reports arrive from
   * the render loop at up to 60Hz; writing through the ref confines each
   * re-render to this leaf instead of the whole panel.
   */
  sink: React.MutableRefObject<ToolOverlaySink | null>;
  /** The panel's tool mode. A hover only draws while it is `"measure"`. */
  toolMode: ToolMode;
  /** Removes the one measurement whose label's "x" was clicked. */
  onRemoveMeasurement: (id: number) => void;
}

/**
 * The measure tool's DOM layer: measurement labels and the rubberband, the
 * coordinate readout, and the snap marker.
 *
 * A hover draws as both readout and marker, because the tool both inspects
 * and measures: the readout names the point under the cursor, and the
 * marker shows where a click would pin it, which on a point cloud is the
 * snapped vertex rather than the pixel under the cursor. Measurement labels
 * outlive the tool being turned off, so they draw regardless of mode. The
 * layer shares the canvas's box, so the manager's canvas-relative
 * coordinates are used as-is.
 */
export function ToolOverlays({
  sink,
  toolMode,
  onRemoveMeasurement,
}: ToolOverlaysProps) {
  const [hover, setHover] = React.useState<ToolHover | null>(null);
  const [overlay, setOverlay] = React.useState<MeasureOverlayProjection | null>(
    null,
  );
  const [measuredBox, box] = useMeasure<HTMLDivElement>();

  React.useLayoutEffect(
    function publishSink() {
      sink.current = {
        onToolHover: setHover,
        onMeasureOverlay: setOverlay,
      };
      return () => {
        sink.current = null;
      };
    },
    [sink],
  );

  return (
    <div
      className={styles.overlays}
      ref={(node) => node !== null && measuredBox(node)}
    >
      <MeasureOverlay overlay={overlay} onRemove={onRemoveMeasurement} />
      {toolMode === "measure" && hover && (
        <>
          <CoordinateTooltip hover={hover} bounds={box} />
          <div
            className={styles.snapMarker}
            data-testid="measure-snap-marker"
            style={{ left: hover.pickScreenX, top: hover.pickScreenY }}
          />
        </>
      )}
    </div>
  );
}
